import type { UseFormReturn } from "react-hook-form";
import { ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import {
  AGE_RANGE_OPTIONS,
  DISABILITY_STATUS_OPTIONS,
  ETHNICITY_OPTIONS,
  GENDER_OPTIONS,
  PRONOUN_OPTIONS,
  RACE_OPTIONS,
  SEXUAL_ORIENTATION_OPTIONS,
  SOCIOECONOMIC_BACKGROUND_OPTIONS,
  VETERAN_STATUS_OPTIONS,
} from "@/types/people";
import { LabelWithError, SectionHead, TextField } from "./fields";
import { deiOptionSlug, type DeiFormValues } from "./personFormSchema";

type DeiSelectField =
  | "gender"
  | "pronouns"
  | "sexual_orientation"
  | "age_range"
  | "disability_status"
  | "veteran_status"
  | "socioeconomic_background";

const SELECT_FIELDS: ReadonlyArray<{ name: DeiSelectField; options: readonly string[] }> = [
  { name: "gender", options: GENDER_OPTIONS },
  { name: "pronouns", options: PRONOUN_OPTIONS },
  { name: "sexual_orientation", options: SEXUAL_ORIENTATION_OPTIONS },
  { name: "age_range", options: AGE_RANGE_OPTIONS },
  { name: "disability_status", options: DISABILITY_STATUS_OPTIONS },
  { name: "veteran_status", options: VETERAN_STATUS_OPTIONS },
  { name: "socioeconomic_background", options: SOCIOECONOMIC_BACKGROUND_OPTIONS },
];

const MULTI_FIELDS: ReadonlyArray<{ name: "ethnicity" | "race"; options: readonly string[] }> = [
  { name: "ethnicity", options: ETHNICITY_OPTIONS },
  { name: "race", options: RACE_OPTIONS },
];

interface PersonDeiSectionProps {
  form: UseFormReturn<DeiFormValues>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The person form's DEI block — GDPR Art. 9 special-category data, rendered
 * for ORG ADMINS ONLY (the dialog never mounts it otherwise). It sits BELOW
 * the document card as a bordered collapsible, the shell of the AP/AR
 * "Advanced VAT options" and `RecurringSection`: voluntary, opt-in data is
 * chrome about the record, not part of the profile "artifact". The consent
 * checkbox is the first row; once ticked the answers appear in one
 * theme-aware `bg-muted` band (`rounded-lg`, no `-mx-6` bleed — outside the
 * Card). Withdrawing consent clears the answers on save.
 */
export function PersonDeiSection({ form, open, onOpenChange }: PersonDeiSectionProps) {
  const { t } = useTranslation("people");
  const consent = form.watch("dei_data_consent");
  const optionLabel = (field: string, value: string) =>
    t(`form.dei.options.${field}.${deiOptionSlug(value)}`, { defaultValue: value });

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="border rounded-lg" data-testid="person-dei-section">
      <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 hover:bg-muted/50 transition-colors">
        <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />
        <span className="text-sm font-medium">{t("form.sections.dei")}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-4">
        <p className="text-sm text-muted-foreground">{t("form.dei.explainer")}</p>
        <FormField
          control={form.control}
          name="dei_data_consent"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={(checked) => field.onChange(checked === true)} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">{t("form.dei.consent")}</FormLabel>
                <p className="text-xs text-muted-foreground">{t("form.dei.consentHint")}</p>
              </div>
            </FormItem>
          )}
        />

      {consent && (
        <div className="rounded-lg bg-muted px-6 py-6 space-y-6">
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SELECT_FIELDS.map(({ name, options }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    {/* Same flex label row as TextField, so the grid's labels align. */}
                    <LabelWithError label={t(`form.dei.fields.${name}`)} />
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("form.dei.selectPlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {options.map((option) => (
                          <SelectItem key={option} value={option}>
                            {optionLabel(name, option)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            ))}
            <TextField
              control={form.control}
              name="primary_language"
              label={t("form.dei.fields.primary_language")}
              placeholder={t("form.dei.placeholders.primaryLanguage")}
            />
            <TextField
              control={form.control}
              name="religious_affiliation"
              label={t("form.dei.fields.religious_affiliation")}
              placeholder={t("form.dei.placeholders.religiousAffiliation")}
            />
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {MULTI_FIELDS.map(({ name, options }) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <section className="space-y-2">
                  <SectionHead className="mb-0">{t(`form.dei.fields.${name}`)}</SectionHead>
                  <p className="text-sm text-muted-foreground">{t("form.dei.selectAll")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    {options.map((option) => {
                      const id = `dei-${name}-${deiOptionSlug(option)}`;
                      const checked = field.value.includes(option);
                      return (
                        <div key={option} className="flex items-center space-x-2">
                          <Checkbox
                            id={id}
                            checked={checked}
                            onCheckedChange={(next) =>
                              field.onChange(
                                next === true ? [...field.value, option] : field.value.filter((v) => v !== option),
                              )
                            }
                          />
                          <Label htmlFor={id} className="font-normal">
                            {optionLabel(name, option)}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            />
          ))}
          </div>
        </div>
      )}
      </CollapsibleContent>
    </Collapsible>
  );
}
